import { NgTemplateOutlet } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { QUEUED_JOBS_PAGE_SIZE, QueuedJobsService } from '@app/v2/shared/services/queued-jobs.service'
import { ErrorResponse } from '@app/v2/shared/types/generic-response.type'
import { QueuedJobModel } from '@app/v2/shared/types/queued-job.model'
import { QUEUED_JOB_ICONS, QueuedJob, QueuedJobsPanelViewState, QueuedJobsResponse } from '@app/v2/shared/types/queued-job.type'
import { AuthService } from '@auth/services/auth.service'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { interval, Observable, Subscription } from 'rxjs'
import { QueuedJobCardComponent } from '../queued-job-card/queued-job-card.component'

@Component({
  selector: 'app-queued-jobs-panel',
  standalone: true,
  imports: [NgTemplateOutlet, QueuedJobCardComponent, SafeDomPipe, NgxSkeletonLoaderModule],
  templateUrl: './queued-jobs-panel.component.html',
  styleUrl: './queued-jobs-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-flex tw-flex-col tw-min-h-0' }
})
export class QueuedJobsPanelComponent implements OnInit {
  public jobs = signal<QueuedJob[]>([])
  public isLoading = signal<boolean>(false)
  public isLoadingMore = signal<boolean>(false)
  public errorMessage = signal<string | null>(null)
  public hasMore = computed(() => this._currentPage() * QUEUED_JOBS_PAGE_SIZE < this._total())
  public hasRunningJobs = computed(() => this._queuedJobsService.processingCount() > 0 || this.jobs().some((job) => job.status === 'processing'))
  public viewState = computed<QueuedJobsPanelViewState>(() => {
    if (this.isLoading()) return 'loading'
    if (this.errorMessage()) return 'error'
    return this.jobs().length ? 'list' : 'empty'
  })
  public readonly icons = QUEUED_JOB_ICONS
  public readonly emptyStateMessage = QueuedJobModel.EMPTY_STATE_MESSAGE
  public readonly loadingLabel = QueuedJobModel.LOADING_LABEL
  public readonly seePreviousJobsLabel = QueuedJobModel.SEE_PREVIOUS_JOBS_LABEL
  public readonly skeletonTheme = QueuedJobModel.SKELETON_THEME

  private readonly _queuedJobsService = inject(QueuedJobsService)
  private readonly _authService = inject(AuthService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _currentPage = signal<number>(1)
  private readonly _total = signal<number>(0)
  private _loadSubscription?: Subscription
  private _loadMoreSubscription?: Subscription
  private _refreshSubscription?: Subscription

  constructor() {
    effect((onCleanup) => {
      if (!this.hasRunningJobs()) return
      const refreshTimer = interval(QueuedJobModel.PANEL_REFRESH_INTERVAL_MS).subscribe(() => this._refreshLoadedJobs())
      onCleanup(() => refreshTimer.unsubscribe())
    })
  }

  ngOnInit(): void {
    this._destroyRef.onDestroy(() => this._cancelRequests())
    this.reload()
  }

  /**
   * Reloads the first page of the organization's queued jobs, cancelling any in-flight request.
   */
  public reload(): void {
    this._cancelRequests()
    const request$ = this._pageRequest$(1)
    if (!request$) return

    this.errorMessage.set(null)
    this.isLoadingMore.set(false)
    this.isLoading.set(true)
    this._loadSubscription = request$.subscribe({
      next: (response) => {
        this._applyPage(response, 1, response.data)
        this.isLoading.set(false)
      },
      error: (error: HttpErrorResponse) => {
        this._applyPage(null, 1, [])
        this.errorMessage.set((error.error as ErrorResponse | null)?.message || QueuedJobModel.LOAD_ERROR_FALLBACK_MESSAGE)
        this.isLoading.set(false)
      }
    })
  }

  /**
   * Appends the next page of jobs, skipping any job already in the list. Failures are ignored.
   */
  public loadMore(): void {
    if (this.isLoadingMore() || !this.hasMore()) return
    const nextPage = this._currentPage() + 1
    const request$ = this._pageRequest$(nextPage)
    if (!request$) return

    this.isLoadingMore.set(true)
    this._loadMoreSubscription = request$.subscribe({
      next: (response) => {
        const loadedIds = new Set(this.jobs().map((job) => job.id))
        this._applyPage(response, nextPage, [...this.jobs(), ...response.data.filter((job) => !loadedIds.has(job.id))])
        this.isLoadingMore.set(false)
      },
      error: () => this.isLoadingMore.set(false)
    })
  }

  /**
   * Silently refreshes page 1 (where running jobs are sorted) and keeps the cards loaded from later pages.
   * Skipped while the tab is hidden or another request is running; a result is discarded if the user loaded
   * more pages meanwhile.
   */
  private _refreshLoadedJobs(): void {
    if (document.visibilityState !== 'visible') return
    if (this.isLoading() || this.isLoadingMore() || this.errorMessage() || this._isRefreshing()) return

    const loadedPages = this._currentPage()
    const request$ = this._pageRequest$(1)
    if (!request$) return

    this._refreshSubscription = request$.subscribe({
      next: (response) => {
        if (this._currentPage() !== loadedPages || this.isLoading() || this.isLoadingMore()) return
        const firstPageIds = new Set(response.data.map((job) => job.id))
        const laterPageJobs = this.jobs()
          .slice(QUEUED_JOBS_PAGE_SIZE)
          .filter((job) => !firstPageIds.has(job.id))
        this._applyPage(response, loadedPages, [...response.data, ...laterPageJobs])
      },
      error: () => undefined
    })
  }

  /**
   * Checks if a refresh request is in progress.
   * @returns True if a refresh request is in progress, false otherwise.
   */
  private _isRefreshing(): boolean {
    return !!this._refreshSubscription && !this._refreshSubscription.closed
  }

  /**
   * Fetches the queued jobs for the current organization.
   * @param page The page number to fetch.
   * @returns An observable of the queued jobs response.
   */
  private _pageRequest$(page: number): Observable<QueuedJobsResponse> | null {
    const organizationSlug = this._authService.currentWorkspace?.slug
    return organizationSlug ? this._queuedJobsService.getQueuedJobs(organizationSlug, page) : null
  }

  /**
   * Applies the page data to the component state.
   * @param response The queued jobs response.
   * @param page The page number.
   * @param jobs The queued jobs.
   */
  private _applyPage(response: QueuedJobsResponse | null, page: number, jobs: QueuedJob[]): void {
    this._currentPage.set(page)
    this._total.set(response?.total ?? 0)
    this.jobs.set(jobs)
  }

  /**
   * Cancels the queued jobs requests.
   */
  private _cancelRequests(): void {
    this._loadSubscription?.unsubscribe()
    this._loadMoreSubscription?.unsubscribe()
    this._refreshSubscription?.unsubscribe()
  }
}
